import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { format as formatDate } from 'date-fns';

import { CardContainer, CardContent, PageView, PageViewHeader } from '../../../../../components/Layout';
import Paginator from '../../../../../components/Paginator';
import { Table } from '../../../../../components/Table';
import { AppState } from '../../../../../store/RootReducer';
import { connect } from 'react-redux';
import LoadingPageView from '../../../../../components/LoadingPageView';
import ErrorPageView from '../../../../../components/ErrorPageView';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import { TriggerDisplayToastPayload } from '../../../../../components/toast/presentation/store/types';
import { triggerDisplayToast } from '../../../../../components/toast/presentation/store/actions';
import usePrevious from '../../../../../hooks/usePrevious';
import Modal from '../../../../../components/Modal';
import { OfficeServerDetails, UpdateOfficeServerRequest } from '../../../domain/repositories/ConfigRepository';
import { resetGetAllOfficeServers, triggerGetAllOfficeServers, updateOfficeServers } from '../../store/getAllOfficeServers/actions';
import SwitchControl from '../../../../../components/SwitchControl';
import { triggerUpdateOfficeServer } from '../../store/updateOfficeServer/actions';
import UpdateModalContent from './UpdateModalContent';
import CreateModalContent from './CreateModalContent';
import DetailsModalContent from './DetailsModalContent';
import Button from '../../../../../components/Button';
import { useHistory } from 'react-router-dom';
import { OFFICE_SERVERS__AUDIT_LOGS } from '../../../../Routes';
import { triggerCreateOfficeServer } from '../../store/createOfficeServer/actions';
import { AxiosError } from 'axios';



const Container = styled(PageView)``;

const ContentContainer = styled(CardContainer)`
  padding: 0px;
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-bottom: 3rem;

  & > *:first-child {
    margin-right: 20px;
  }
`;

const TableContainer = styled.div`
  position: relative;
  overflow: auto;

  td a {
    white-space: nowrap;
  }

  td.owner-column {
    min-width: 150px;
  }

  td.actions-container a {
    margin-right: 40px;
  }

  td.actions-container a:last-child {
    margin-right: 0px;
  }


  td.actions-container a.delete-action {
    color: red;
  }

  td.toggle-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    & > p {
      margin-right: 10px;
    }
  }
`;

interface StoreStateProps {
  getAllOfficeServersPending: boolean;
  getAllOfficeServersError: boolean;
  officeServers: OfficeServerDetails[] | null;

  createOfficeServerPending: boolean;
  createOfficeServerError: AxiosError | null;
  createdOfficeServerDetails: OfficeServerDetails | null;

  updateOfficeServerPending: boolean;
  updateOfficeServerError: boolean;
}

interface StoreDispatchProps {
  getAllOfficeServers: () => void;
  createOfficeServer: (officeServer: string) => void;
  updateOfficeServer: (request: UpdateOfficeServerRequest) => void;
  updateOfficeServers: (officeServers: OfficeServerDetails[]) => void;
  resetGetAllOfficeServers: () => void;
  displayToast: (toastPayload: TriggerDisplayToastPayload) => void;
}

interface OwnProps {

}

type Props = StoreStateProps & StoreDispatchProps & OwnProps;

export function OfficeServersPage(props: Props) {
  const {
    getAllOfficeServersPending,
    getAllOfficeServersError,
    officeServers,

    createOfficeServerPending,
    createOfficeServerError,
    createdOfficeServerDetails,

    updateOfficeServerPending,
    updateOfficeServerError,

    getAllOfficeServers,
    createOfficeServer,
    updateOfficeServer,
    updateOfficeServers,
    resetGetAllOfficeServers,
    displayToast
  } = props;

  const history = useHistory();

  const prevGetAllOfficeServersPending = usePrevious(getAllOfficeServersPending);
  const prevCreateOfficeServerPending = usePrevious(createOfficeServerPending);
  const prevUpdateOfficeServerPending = usePrevious(updateOfficeServerPending);

  const [updateServerData, setUpdateServerData] = useState<OfficeServerDetails>();
  const [detailsServerData, setDetailsServerData] = useState<OfficeServerDetails>();

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDetailseModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const onUpdateServer = (serverDetails: OfficeServerDetails) => {
    setUpdateServerData(serverDetails);
    setIsUpdateModalOpen(true);
  }

  const onUpdateConfirm = () => {
    if (!updateServerData) return;

    const { officeServer, serverStatus } = updateServerData;

    updateOfficeServer({
      officeServer: officeServer,
      serverStatus: !serverStatus
    });
  }

  const onCreateServer = () => {
    setIsCreateModalOpen(true);
  };

  const onCreateConfirm = (officeServer: string) => {
    createOfficeServer(officeServer);
  };

  const onRefreshServerList = () => {
    getAllOfficeServers();
  };

  const viewServerDetails = (serverDetails: OfficeServerDetails) => {
    setDetailsServerData(serverDetails);
    setIsDetailsModalOpen(true);
  };

  const onViewServerLogs = (officeServer: string) => {
    history.push({ pathname: `${OFFICE_SERVERS__AUDIT_LOGS}/${officeServer}` });
  };

  useEffect(() => {
    getAllOfficeServers();
    return () => resetGetAllOfficeServers();
  }, []);



  useEffect(() => {
    if (!(prevGetAllOfficeServersPending && !getAllOfficeServersPending)) return;

    if (getAllOfficeServersError) {
      displayToast({ toastType: "ERROR" });
    }
  }, [getAllOfficeServersPending]);



  useEffect(() => {
    if (!(prevCreateOfficeServerPending && !createOfficeServerPending)) return;

    if (createOfficeServerError) {
      if (createOfficeServerError.response?.status === 409) {
        displayToast({ toastType: "ERROR", message: 'Office server with this IP/name already exists' });
      } else {
        displayToast({ toastType: "ERROR" });
      }
      
      return;
    }

    if (!officeServers || !createdOfficeServerDetails) return;

    const updatedServerList = [createdOfficeServerDetails, ...officeServers];

    updateOfficeServers(updatedServerList);

    setIsCreateModalOpen(false);

    displayToast({ toastType: "SUCCESS", message: "Server created successfully" });
  }, [createOfficeServerPending]);



  useEffect(() => {
    if (!(prevUpdateOfficeServerPending && !updateOfficeServerPending)) return;

    if (updateOfficeServerError) {
      displayToast({ toastType: "ERROR" });
      return;
    }

    if (!officeServers) return;

    const updatedServerList = officeServers.map(server => {
      if (server.config_id != updateServerData?.config_id) return server;

      return { ...server, serverStatus: !server.serverStatus }
    });

    updateOfficeServers(updatedServerList);

    setIsUpdateModalOpen(false);

    displayToast({ toastType: "SUCCESS", message: "Server updated successfully" });
  }, [updateOfficeServerPending]);


  return (
    <Container>
      <PageViewHeader>
        <h3>Office Servers</h3>
      </PageViewHeader>

      <Modal
        id="create-server-modal"
        open={isCreateModalOpen}
        closeButtonHandler={() => setIsCreateModalOpen(false)}
        modalTitle="Create Server"
        wrapperStyle={{ marginTop: "100px" }}
      >
        <CreateModalContent
          isProcessing={createOfficeServerPending}
          cancelButtonHandler={() => setIsCreateModalOpen(false)}
          onSubmit={onCreateConfirm}
        />
      </Modal>

      <Modal
        id="update-server-modal"
        open={isUpdateModalOpen}
        closeButtonHandler={() => setIsUpdateModalOpen(false)}
        modalTitle={`${updateServerData?.serverStatus ? 'Disable' : 'Enable'} ${updateServerData?.officeServer}?`}
        wrapperStyle={{ marginTop: "100px" }}
      >
        <UpdateModalContent
          serverName={updateServerData?.officeServer}
          isServerEnabled={updateServerData?.serverStatus}
          isProcessing={updateOfficeServerPending}
          cancelButtonHandler={() => setIsUpdateModalOpen(false)}
          updateButtonHandler={onUpdateConfirm}
        />
      </Modal>

      <Modal
        id="server-details-modal"
        open={isDetailseModalOpen}
        closeButtonHandler={() => setIsDetailsModalOpen(false)}
        modalTitle={`Server Details`}
        wrapperStyle={{ marginTop: "100px" }}
      >
        <DetailsModalContent
          serverDetails={detailsServerData}
        />
      </Modal>

      {getAllOfficeServersPending && !officeServers && (
        <LoadingPageView />
      )}

      {!getAllOfficeServersPending && getAllOfficeServersError && !officeServers && (
        <ErrorPageView onRetry={() => getAllOfficeServers()} />
      )}

      {officeServers && officeServers.length === 0 && (
        <p>No reports found</p>
      )}

      {officeServers && officeServers.length > 0 && (
        <>
          <ControlsContainer>
            <Button
              id="create-server-button"
              className="secondary"
              text="Refresh List"
              onClick={onRefreshServerList}
            />

            <Button
              id="create-server-button"
              text="Create New Server"
              onClick={onCreateServer}
            />
          </ControlsContainer>

          <ContentContainer>

            <CardContent style={{ padding: '0px' }}>
              <TableContainer>
                <LoadingOverlay show={getAllOfficeServersPending && !!officeServers} />

                <Table id="reports-table">
                  <thead>
                    <tr>
                      <td>Server</td>
                      <td>Last Modified On</td>
                      <td className="break-word owner-column">Last Modified By</td>
                      <td className="center">Status</td>
                      <td className="center"></td>
                    </tr>
                  </thead>
                  <tbody>
                    {officeServers.map((serverDetails, index) => {
                      const {
                        config_id, officeServer, lastModifiedOn, lastModifiedBy, serverStatus
                      } = serverDetails;

                      const lastModifiedOnDateObj = new Date(lastModifiedOn);
                      const lastModifiedOnDateString = formatDate(lastModifiedOnDateObj, 'dd-MM-yyyy');
                      const lastModifiedOnTimeString = formatDate(lastModifiedOnDateObj, 'hh:mm aa');

                      return (
                        <tr key={config_id} id={`server-row-${officeServer}`}>
                          <td>
                            <p>{officeServer}</p>
                          </td>

                          <td>
                            <p>{lastModifiedOnDateString}</p>
                            <p>{lastModifiedOnTimeString}</p>
                          </td>

                          <td className="break-word owner-column">{lastModifiedBy}</td>

                          <td className="center">
                            {/* <p>{serverStatus ? 'Active' : 'Inactive'}</p> */}

                            <SwitchControl
                              className="toggle"
                              active={serverStatus}
                              onClickHandler={() => onUpdateServer(serverDetails)}
                            />
                          </td>

                          <td className="center actions-container">
                            <a href="#" onClick={() => viewServerDetails(serverDetails)} >Details</a>
                            <a href="#" onClick={() => onViewServerLogs(serverDetails.officeServer)} >Audit Logs</a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableContainer>

            </CardContent>
          </ContentContainer>
        </>
      )}

    </Container>
  );
}


const mapStateToProps = (state: AppState): StoreStateProps => ({
  getAllOfficeServersPending: state.getAllOfficeServers.getAllOfficeServersPending,
  getAllOfficeServersError: state.getAllOfficeServers.getAllOfficeServersError,
  officeServers: state.getAllOfficeServers.officeServers,

  createOfficeServerPending: state.createOfficeServer.createOfficeServerPending,
  createOfficeServerError: state.createOfficeServer.createOfficeServerError,
  createdOfficeServerDetails: state.createOfficeServer.createdOfficeServerDetails,

  updateOfficeServerPending: state.updateOfficeServer.updateOfficeServerPending,
  updateOfficeServerError: state.updateOfficeServer.updateOfficeServerError,

});

const mapDispatchToProps = (
  dispatch: (action: any) => void
): StoreDispatchProps => ({
  getAllOfficeServers() {
    dispatch(triggerGetAllOfficeServers());
  },
  resetGetAllOfficeServers() {
    dispatch(resetGetAllOfficeServers());
  },
  createOfficeServer(officeServer: string) {
    dispatch(triggerCreateOfficeServer(officeServer));
  },
  updateOfficeServer(request: UpdateOfficeServerRequest) {
    dispatch(triggerUpdateOfficeServer(request));
  },
  updateOfficeServers(officeServers: OfficeServerDetails[]) {
    dispatch(updateOfficeServers(officeServers));
  },
  displayToast(toastPayload: TriggerDisplayToastPayload) {
    dispatch(triggerDisplayToast(toastPayload));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(OfficeServersPage);